import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, Loader2, Award, BookOpen, Share2, Copy, Check } from "lucide-react";

/**
 * AdventureCertificate — a printable pop-up-book style certificate for
 * a completed adventure.  Payload comes from
 * GET /api/adventures/{slug}/certificate/me — 404 unless the learner has
 * marked the adventure complete.  Users can print via window.print().
 */
export default function AdventureCertificate() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api.get(`/adventures/${slug}/certificate/me`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => {
        if (cancelled) return;
        const s = e?.response?.status;
        setData({ notfound: true, reason: s === 401 ? "auth" : s === 404 ? "incomplete" : "error" });
      });
    return () => { cancelled = true; };
  }, [slug]);

  const shareUrl = data?.share_token ? `${window.location.origin}/c/${data.share_token}` : "";

  const onCopyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — paste it wherever you brag.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — please copy from the address bar."); }
  };

  const onShareLinkedIn = () => {
    if (!shareUrl) return;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
  };
  const onShareTwitter = () => {
    if (!shareUrl) return;
    const text = `I finished ${data.title} on Durga Programming Adventures.`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener");
  };

  const onPrint = () => {
    toast.info("Opening the print dialog…");
    window.print();
  };

  if (!data) return <div className="container-narrow py-24 text-center text-ink2"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  if (data.notfound) {
    const msg = data.reason === "auth" ? "Sign in to view your certificate."
              : data.reason === "incomplete" ? "You haven't finished this adventure yet."
              : "Something went wrong loading your certificate.";
    return (
      <div className="container-narrow py-24 text-center" data-testid="certificate-404">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-3">{msg}</h1>
        <div className="mt-4 flex items-center justify-center gap-3">
          <Link to={`/adventures/${slug}`} className="btn-outline-ink text-sm inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to the adventure
          </Link>
        </div>
      </div>
    );
  }

  const totalChapters = (data.act_snapshots || []).reduce((n, a) => n + (a.chapters?.length || 0), 0);
  const dateStr = new Date(data.completed_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="bg-cream py-8 md:py-14 print:py-0" data-testid={`certificate-${slug}`}>
      {/* Toolbar — hidden on print */}
      <div className="container-narrow mb-6 flex items-center justify-between gap-3 flex-wrap print:hidden">
        <Link to={`/adventures/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-ink2 hover:text-ink">
          <ArrowLeft className="w-4 h-4" /> Back to the adventure
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {shareUrl && (
            <>
              <button
                onClick={onCopyShare}
                className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
                data-testid="certificate-copy-share"
                title={shareUrl}
              >
                {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy share link</>}
              </button>
              <button
                onClick={onShareLinkedIn}
                className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
                data-testid="certificate-share-linkedin"
              >
                <Share2 className="w-3.5 h-3.5" /> LinkedIn
              </button>
              <button
                onClick={onShareTwitter}
                className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
                data-testid="certificate-share-twitter"
              >
                <Share2 className="w-3.5 h-3.5" /> Twitter
              </button>
            </>
          )}
          <button
            onClick={onPrint}
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            data-testid="certificate-print"
          >
            <Printer className="w-4 h-4" /> Print certificate
          </button>
        </div>
      </div>

      {/* Certificate paper */}
      <div className="container-narrow print:max-w-none print:mx-0 print:w-full">
        <div
          className="relative rounded-3xl border-4 border-ink bg-cream shadow-[12px_12px_0_0_#1F1A17] print:shadow-none print:border-2 overflow-hidden"
          data-testid="certificate-paper"
        >
          {/* Corner ornaments */}
          <CornerOrn className="top-4 left-4" />
          <CornerOrn className="top-4 right-4 rotate-90" />
          <CornerOrn className="bottom-4 left-4 -rotate-90" />
          <CornerOrn className="bottom-4 right-4 rotate-180" />

          {/* Big ghost kicker */}
          <div className="absolute top-6 right-8 md:right-14 font-heading text-[9rem] md:text-[12rem] font-semibold text-ink/[0.055] leading-none select-none pointer-events-none">
            {(data.kicker || "").split(" ").pop()}
          </div>

          <div className="p-8 md:p-14 relative">
            <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-marigold mb-3 inline-flex items-center gap-2">
              <Award className="w-3.5 h-3.5" /> Certificate of Expedition
            </div>

            <p className="text-ink2 text-sm md:text-base italic mb-2">This is to certify that</p>
            <div className="font-heading text-4xl md:text-6xl font-semibold text-ink leading-tight mb-6" data-testid="certificate-learner-name">
              {data.learner_name}
            </div>
            <p className="text-ink2 text-sm md:text-base mb-1">has walked, at their own pace, the whole of</p>
            <div className="font-heading text-3xl md:text-5xl font-semibold text-marigold italic leading-tight mb-4 gold-underline inline-block" data-testid="certificate-adv-title">
              {data.title}
            </div>
            <p className="text-ink2 italic max-w-3xl">{data.one_line}</p>

            {/* Two-column: chapters read + world map */}
            <div className="mt-10 grid md:grid-cols-3 gap-6 md:gap-8">
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-3">
                  The {totalChapters} chapters that walked with you
                </div>
                <ol className="space-y-4" data-testid="certificate-chapters">
                  {(data.act_snapshots || []).map((act) => (
                    <li key={act.n}>
                      <div className="text-[9px] font-mono font-bold text-ink/50 uppercase tracking-[0.22em] mb-1">
                        Act {act.n} · {act.eyebrow}
                      </div>
                      <div className="font-heading text-lg font-semibold text-ink mb-2">{act.title}</div>
                      <ul className="text-sm text-ink2 space-y-1 pl-4 border-l-2 border-marigold/40">
                        {(act.chapters || []).map((c) => (
                          <li key={c.slug} className="leading-snug">
                            <span className="font-mono text-[10px] text-ink/50 mr-2">{c.code}</span>
                            {c.title}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="md:col-span-1">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-3">
                  Completed on
                </div>
                <div className="font-heading text-lg text-ink mb-6">{dateStr}</div>

                {/* Tiny stylised map — a little sketched India */}
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-2">Filed from</div>
                <div className="rounded-2xl border-2 border-ink bg-parchment/50 p-4">
                  <svg viewBox="0 0 200 220" className="w-full h-auto">
                    {/* Very stylised India outline */}
                    <path
                      d="M60 20 L110 15 L130 30 L155 45 L170 75 L165 105 L145 135 L155 165 L140 200 L110 210 L90 200 L80 175 L65 155 L45 130 L35 95 L40 60 Z"
                      fill="#F4A261" fillOpacity="0.12" stroke="#1F1A17" strokeWidth="2" strokeLinejoin="round"
                    />
                    {/* North-east tail */}
                    <path d="M160 60 L185 75 L180 100 L165 95" fill="#F4A261" fillOpacity="0.12" stroke="#1F1A17" strokeWidth="1.5" strokeLinejoin="round" />
                    {/* A few 'stops' as dots */}
                    {[[95, 55], [80, 90], [110, 110], [95, 140], [130, 155], [160, 80]].map(([x, y], i) => (
                      <g key={i}>
                        <circle cx={x} cy={y} r={4} fill="#E76F51" stroke="#1F1A17" strokeWidth="1.2" />
                      </g>
                    ))}
                    <text x="100" y="15" textAnchor="middle" style={{ fontFamily: "Caveat, cursive", fontSize: 14, fill: "#E76F51" }}>Durga's road</text>
                  </svg>
                </div>
              </div>
            </div>

            {/* Signature */}
            <div className="mt-10 pt-6 border-t border-ink/25 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="font-hand text-3xl text-ink mb-1">Pradhan</div>
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-ink2/70">
                  Ms. Pradhan · teacher-in-residence
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-ink2/70 mb-1 inline-flex items-center gap-1.5">
                  <BookOpen className="w-3 h-3" /> Durga Programming Adventures
                </div>
                <div className="text-[10px] font-mono text-ink/50">certificate · {slug}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print-only CSS */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          body { background: #FDF6E7 !important; }
          .container-narrow { max-width: none !important; padding: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function CornerOrn({ className = "" }) {
  return (
    <div className={`absolute w-14 h-14 text-marigold pointer-events-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 60 60" width="100%" height="100%">
        <path d="M4 4 L26 4 M4 4 L4 26 M4 4 L18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="8" cy="8" r="2.5" fill="currentColor" />
      </svg>
    </div>
  );
}
